import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import {
  Business,
  EmojiEvents,
  SupportAgent,
  Lightbulb,
  Security,
  Speed,
  FormatQuote,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';

const SERIF = "'Playfair Display', 'Nunito', serif";

const stats = [
  { number: '5+', label: 'Happy Clients' },
  { number: '06+', label: 'Projects Delivered' },
  { number: '05+', label: 'Team Experts' },
  { number: '95%', label: 'Client Satisfaction' },
];

const values = [
  {
    icon: <Lightbulb />,
    title: 'Innovation',
    description: 'We constantly explore new technologies and strategies to stay ahead of the curve.',
  },
  {
    icon: <Security />,
    title: 'Trust',
    description: 'Building long-term relationships through transparency and reliability.',
  },
  {
    icon: <Speed />,
    title: 'Efficiency',
    description: 'Delivering results quickly without compromising on quality.',
  },
  {
    icon: <SupportAgent />,
    title: 'Support',
    description: 'Dedicated support to ensure your business runs smoothly, every step of the way.',
  },
];

const whyChooseUs = [
  {
    icon: <Business sx={{ fontSize: 56 }} />,
    title: 'Expert Team',
    description:
      'Our certified professionals bring years of digital marketing expertise to every campaign, blending craft with curiosity.',
  },
  {
    icon: <EmojiEvents sx={{ fontSize: 56 }} />,
    title: 'Proven Results',
    description:
      'We deliver measurable growth through data-driven strategies, continuous optimisation, and an obsession with outcomes.',
  },
  {
    icon: <SupportAgent sx={{ fontSize: 56 }} />,
    title: 'Dedicated Support',
    description:
      'We stay close after launch too — tailoring our service to your business as it grows, not just at the pitch.',
  },
];

export default function AboutUs() {
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const reveal = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 28 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: '-80px' },
          transition: { duration: 0.7, delay, ease: 'easeOut' },
        };

  return (
    <>
      <Helmet>
        <title>About Us | SLGT Digital Marketing Agency</title>
        <meta
          name="description"
          content="Learn about SLGT Digital Marketing Agency based in Anakapalli, Visakhapatnam. Your trusted partner for SEO, Social Media Marketing, PPC, and digital growth solutions."
        />
        <meta
          name="keywords"
          content="About SLGT, Digital Marketing Agency Anakapalli, SEO Company Visakhapatnam, Social Media Marketing, PPC Services, Online Marketing Vizag"
        />
        <link rel="canonical" href="https://www.slgt.in/aboutus" />
      </Helmet>

      <Box sx={{ bgcolor: '#FDFBF7' }}>
        {/* Hero */}
        <Box
          sx={{
            position: 'relative',
            minHeight: { xs: '64vh', md: '72vh' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            overflow: 'hidden',
            bgcolor: '#FDFBF7',
          }}
        >
          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: 10 }}>
            <motion.div {...reveal(0)}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/slgt-logo.png`}
                alt=""
                aria-hidden="true"
                sx={{ width: 56, height: 56, objectFit: 'contain', mb: 3 }}
              />
              <Box sx={{ width: 60, height: 2, bgcolor: '#FD8112', mx: 'auto', mb: 3 }} />
              <Typography
                sx={{
                  color: '#FD8112',
                  letterSpacing: 3,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  mb: 3,
                  textTransform: 'uppercase',
                }}
              >
                About SLGT
              </Typography>
            </motion.div>

            <motion.div {...reveal(0.1)}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: SERIF,
                  fontWeight: 700,
                  color: '#14141F',
                  lineHeight: 1.2,
                  mb: 3,
                  fontSize: { xs: '2.25rem', sm: '3rem', md: '3.75rem' },
                }}
              >
                Where Bold Ideas Meet
                <br />
                <Box
                  component="span"
                  sx={{ position: 'relative', display: 'inline-block', px: { xs: 0.5, md: 1 } }}
                >
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/assets/ovel-bg.png`}
                    alt=""
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      top: '-35%',
                      left: '-12%',
                      width: '124%',
                      height: '170%',
                      zIndex: 0,
                      opacity: 0.9,
                      pointerEvents: 'none',
                    }}
                  />
                  <Box component="span" sx={{ position: 'relative', zIndex: 1, fontStyle: 'italic', color: '#FD8112' }}>
                    Measurable Growth
                  </Box>
                </Box>
              </Typography>
            </motion.div>

            <motion.div {...reveal(0.2)}>
              <Typography
                sx={{
                  color: 'text.secondary',
                  maxWidth: 620,
                  mx: 'auto',
                  fontSize: { xs: '1rem', sm: '1.15rem' },
                  fontWeight: 400,
                }}
              >
                We're a digital marketing collective in Anakapalli, Visakhapatnam —
                turning ambitious brands into industry leaders, one campaign at a time.
              </Typography>
            </motion.div>
          </Container>

          {!prefersReducedMotion && (
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}
            >
              <KeyboardArrowDown sx={{ color: 'rgba(20,20,31,0.35)', fontSize: 36 }} />
            </motion.div>
          )}
        </Box>

        {/* Our Story */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, sm: 12 } }}>
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div {...reveal(0)}>
                <Typography
                  sx={{
                    color: '#FD8112',
                    letterSpacing: 3,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    mb: 2,
                    textTransform: 'uppercase',
                  }}
                >
                  Who We Are
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontFamily: SERIF, fontWeight: 700, mb: 3, fontSize: { xs: '1.9rem', sm: '2.4rem' } }}
                >
                  Small Team, Big Ambitions
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.05rem', color: 'text.secondary', mb: 2.5, lineHeight: 1.9 }}>
                  SLGT Digital Marketing is a full-service agency based in Anakapalli,
                  Visakhapatnam, helping businesses grow their online presence through
                  innovative digital solutions and data-driven strategy.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.05rem', color: 'text.secondary', mb: 3.5, lineHeight: 1.9 }}>
                  With a team of passionate experts, we combine creativity with
                  analytics to deliver outcomes our clients can point to — not just
                  promises they have to trust.
                </Typography>

                <Box
                  sx={{
                    borderLeft: '3px solid #FD8112',
                    pl: 3,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: SERIF,
                      fontStyle: 'italic',
                      fontSize: '1.2rem',
                      color: 'text.primary',
                    }}
                  >
                    "We don't chase trends — we build strategies that outlast them."
                  </Typography>
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div {...reveal(0.15)}>
                <Box
                  sx={{
                    position: 'relative',
                    maxWidth: 460,
                    mx: 'auto',
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      transform: 'translate(18px, 18px)',
                      border: '2px solid #FD8112',
                      borderRadius: 2,
                      zIndex: 0,
                    }}
                  />
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/assets/SLGT%20design.png`}
                    alt="SLGT Digital Marketing brand identity"
                    loading="lazy"
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      width: '100%',
                      display: 'block',
                      borderRadius: 2,
                      boxShadow: '0 24px 48px rgba(20,20,31,0.18)',
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Stats */}
        <Box sx={{ bgcolor: '#12121C', color: '#fff', py: { xs: 6, sm: 8 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 4, sm: 2 }}>
              {stats.map((stat, index) => (
                <Grid item xs={6} md={3} key={stat.label}>
                  <motion.div {...reveal(index * 0.1)}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        px: 2,
                        borderRight: {
                          md: index < stats.length - 1 ? '1px solid rgba(255,255,255,0.14)' : 'none',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: SERIF,
                          fontWeight: 700,
                          color: '#FD8112',
                          fontSize: { xs: '2.2rem', sm: '2.8rem' },
                          lineHeight: 1,
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 1,
                          letterSpacing: 1.5,
                          textTransform: 'uppercase',
                          fontSize: '0.78rem',
                          color: 'rgba(255,255,255,0.65)',
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Core Values */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, sm: 12 } }}>
          <motion.div {...reveal(0)}>
            <Typography
              sx={{
                color: '#FD8112',
                letterSpacing: 3,
                fontWeight: 700,
                fontSize: '0.8rem',
                mb: 2,
                textAlign: 'center',
                textTransform: 'uppercase',
              }}
            >
              What Drives Us
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontFamily: SERIF,
                fontWeight: 700,
                textAlign: 'center',
                mb: 7,
                fontSize: { xs: '1.9rem', sm: '2.4rem' },
              }}
            >
              Our Core Values
            </Typography>
          </motion.div>

          <Grid container spacing={0}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} key={value.title}>
                <motion.div {...reveal(index * 0.1)}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 3,
                      py: 3.5,
                      px: { xs: 0, sm: 3 },
                      borderBottom: '1px solid rgba(20,20,31,0.1)',
                      height: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: SERIF,
                        fontWeight: 700,
                        color: 'rgba(20,20,31,0.15)',
                        fontSize: '2.75rem',
                        lineHeight: 1,
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </Typography>
                    <Box>
                      <Box sx={{ color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        {value.icon}
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {value.title}
                        </Typography>
                      </Box>
                      <Typography color="text.secondary">{value.description}</Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Why Choose Us */}
        <Box sx={{ bgcolor: '#F4F1EA', py: { xs: 8, sm: 12 } }}>
          <Container maxWidth="lg">
            <motion.div {...reveal(0)}>
              <Typography
                sx={{
                  color: '#FD8112',
                  letterSpacing: 3,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  mb: 2,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                }}
              >
                The SLGT Difference
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: SERIF,
                  fontWeight: 700,
                  textAlign: 'center',
                  mb: 8,
                  fontSize: { xs: '1.9rem', sm: '2.4rem' },
                }}
              >
                Why Businesses Choose Us
              </Typography>
            </motion.div>

            {whyChooseUs.map((item, index) => (
              <Grid
                container
                spacing={{ xs: 4, md: 8 }}
                alignItems="center"
                key={item.title}
                direction={index % 2 === 1 ? 'row-reverse' : 'row'}
                sx={{ mb: index < whyChooseUs.length - 1 ? { xs: 6, md: 10 } : 0 }}
              >
                <Grid item xs={12} md={5}>
                  <motion.div {...reveal(0.1)}>
                    <Box
                      sx={{
                        width: { xs: 140, sm: 180 },
                        height: { xs: 140, sm: 180 },
                        mx: { xs: 'auto', md: index % 2 === 1 ? 'auto' : 0 },
                        ml: { md: index % 2 === 1 ? 'auto' : 0 },
                        mr: { md: index % 2 === 1 ? 0 : 'auto' },
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(253,129,18,0.1)',
                        border: '1px solid rgba(253,129,18,0.3)',
                        color: 'primary.main',
                      }}
                    >
                      {item.icon}
                    </Box>
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={7}>
                  <motion.div {...reveal(0.2)}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: SERIF,
                        fontWeight: 700,
                        mb: 1.5,
                        textAlign: { xs: 'center', md: 'left' },
                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: '1.05rem', lineHeight: 1.9, textAlign: { xs: 'center', md: 'left' } }}
                    >
                      {item.description}
                    </Typography>
                  </motion.div>
                </Grid>
              </Grid>
            ))}
          </Container>
        </Box>

        {/* Manifesto / Pull quote */}
        <Container maxWidth="md" sx={{ py: { xs: 8, sm: 12 }, textAlign: 'center' }}>
          <motion.div {...reveal(0)}>
            <FormatQuote sx={{ fontSize: 48, color: 'rgba(253,129,18,0.4)', mb: 1 }} />
            <Typography
              sx={{
                fontFamily: SERIF,
                fontStyle: 'italic',
                fontWeight: 600,
                fontSize: { xs: '1.5rem', sm: '2.1rem' },
                lineHeight: 1.5,
                color: 'text.primary',
                mb: 3,
              }}
            >
              "Great marketing isn't loud. It's memorable."
            </Typography>
            <Divider sx={{ width: 80, mx: 'auto', mb: 2, borderColor: 'primary.main', borderBottomWidth: 2 }} />
            <Typography sx={{ letterSpacing: 2, textTransform: 'uppercase', fontSize: '0.8rem', color: 'text.secondary' }}>
              The SLGT Philosophy
            </Typography>
          </motion.div>
        </Container>

        {/* CTA */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, sm: 12 } }}>
          <motion.div {...reveal(0)}>
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                textAlign: 'center',
                borderRadius: 3,
                p: { xs: 5, sm: 8 },
                bgcolor: '#12121C',
                color: '#fff',
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  top: -60,
                  right: -60,
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(253,129,18,0.35), transparent 70%)',
                  filter: 'blur(6px)',
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  position: 'relative',
                  fontFamily: SERIF,
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '1.8rem', sm: '2.3rem' },
                }}
              >
                Ready to Write Your Growth Story?
              </Typography>
              <Typography sx={{ position: 'relative', color: 'rgba(255,255,255,0.75)', mb: 4, fontSize: '1.05rem' }}>
                Let's discuss how we can help you achieve your digital marketing goals.
              </Typography>
              <Box
                component={Link}
                to="/contact"
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  bgcolor: '#FD8112',
                  color: '#fff',
                  px: 4.5,
                  py: 1.5,
                  borderRadius: 999,
                  textDecoration: 'none',
                  fontWeight: 700,
                  transition: 'transform 0.25s ease',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              >
                Get Started Today
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </>
  );
}
